/**
 * §P.6 · Escribir un aviso.
 *
 * ## Publicar y guardar como borrador son dos botones, no una casilla
 *
 * `publish: false` deja el aviso escrito **y no avisa a nadie**. Con una casilla
 * marcada por defecto, quien la pasa por alto manda el aviso; con dos botones hay
 * que elegir, y el destructivo —interrumpir a 120 familias— no es el que se pulsa
 * por inercia.
 *
 * ## Las audiencias se suman
 *
 * Y se dice en la pantalla, porque un formulario con dos filtros sugiere lo
 * contrario. «Los del martes» y «los verdes» llega a la unión de los dos.
 *
 * ## Corregir no cambia la audiencia
 *
 * El `PATCH` del contrato no la acepta, y la pantalla no la enseña al corregir.
 * Quien ya lo recibió no puede des-recibirlo, y quien no lo recibió no se
 * enteraría del cambio: para eso está retirarlo y publicar otro.
 *
 * ## A cuántas familias llega, antes de pulsar · 13-10-2026
 *
 * Mientras se eligen las audiencias, `POST /announcements/preview` dice a cuántas
 * familias llegaría, **con el mismo cálculo** que el aviso publicado, y la cifra va
 * en el propio botón: «Publicar y avisar a 34 familias». Es lo que frena un aviso
 * mandado a todo el club por error. Un borrador se publica desde aquí al corregirlo,
 * o desde la lista.
 */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { cuenta } from '@/shared/ui/formato';
import { FaltaAntes } from '@/shared/ui/FaltaAntes';
import {
  AMBITOS,
  etiquetaDeAudiencia,
  type AmbitoDeAudiencia,
  type Aviso,
} from '@/club/avisos/tipos';
import type { Grupo } from '@/club/grupos/tipos';
import type { Cinturon } from '@/club/grados/tipos';
import type { SistemaDeGrados } from '@/club/grados/tipos';

/** Una fila de audiencia en el formulario. */
interface FilaDeAudiencia {
  scope: AmbitoDeAudiencia;
  group_id: string;
  belt_rank_id: string;
}

const nueva = (): FilaDeAudiencia => ({
  scope: 'all',
  group_id: '',
  belt_rank_id: '',
});

/** Las filas del formulario, como las pide el contrato: lo que sobra, fuera. */
const delContrato = (audiencias: readonly FilaDeAudiencia[]) =>
  audiencias.map((a) => ({
    scope: a.scope,
    ...(a.scope === 'group' && a.group_id ? { group_id: a.group_id } : {}),
    ...(a.scope === 'belt_rank' && a.belt_rank_id
      ? { belt_rank_id: a.belt_rank_id }
      : {}),
  }));

/** «a 34 familias», para el botón. */
const aFamilias = (n: number) => `a ${cuenta(n, 'familia', 'familias')}`;

export function FormularioDeAviso({
  club,
  aviso,
  alTerminar,
  alCancelar,
}: {
  club: string;
  /** `null` es uno nuevo; con aviso, se corrige. */
  aviso: Aviso | null;
  alTerminar: () => void;
  alCancelar: () => void;
}) {
  const corrigiendo = aviso !== null;

  const [titulo, setTitulo] = useState(aviso?.title ?? '');
  const [cuerpo, setCuerpo] = useState(aviso?.body ?? '');
  const [fijado, setFijado] = useState(aviso?.pinned ?? false);
  const [pideAcuse, setPideAcuse] = useState(aviso?.requires_ack ?? false);
  const [audiencias, setAudiencias] = useState<FilaDeAudiencia[]>([nueva()]);
  const [problema, setProblema] = useState<Problema | null>(null);

  const grupos = useQuery({
    queryKey: ['grupos', club, 'para-avisos'],
    enabled: !corrigiendo,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).groups.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Grupo[];
    },
  });

  const sistemas = useQuery({
    queryKey: ['grados', club, 'en-uso'],
    enabled: !corrigiendo,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['belt-schemes'].get({
        /*
         * Con las plantillas: un club puede usar la plantilla global tal cual
         * —Ajustes → Disciplinas lo ofrece así a uno recién llegado— y sin ellas
         * esa escalera no aparecía. El club no podía otorgar un grado desde la
         * ficha ni avisar por cinturón. 24-09-2026.
         */
        query: { include_templates: true },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return (data as unknown as SistemaDeGrados[]).filter((s) => s.in_use);
    },
  });

  const cinturones = useQuery({
    queryKey: ['cinturones', club, sistemas.data?.[0]?.id],
    enabled: !corrigiendo && Boolean(sistemas.data?.[0]),
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['belt-schemes']({ schemeId: sistemas.data![0]!.id })
        .ranks.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Cinturon[];
    },
  });

  const borrador = corrigiendo && aviso.published_at === null;

  const guardar = useMutation({
    mutationFn: async (publicar: boolean) => {
      if (corrigiendo) {
        // Sin audiencia: el contrato no la acepta al corregir. Ver la cabecera.
        const r = await api.v1
          .clubs({ club })
          .announcements({ announcementId: aviso.id })
          .patch({
            title: titulo.trim(),
            body: cuerpo.trim(),
            pinned: fijado,
            requires_ack: pideAcuse,
          });
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        if (!publicar || !borrador) return r.data;

        // Un borrador corregido y publicado de una vez: primero el texto, luego
        // el aviso, para que llegue lo que se acaba de escribir.
        const p = await api.v1
          .clubs({ club })
          .announcements({ announcementId: aviso.id })
          .publish.post(undefined, {
            headers: { 'idempotency-key': crypto.randomUUID() },
          });
        if (p.error) throw new ErrorDeApi(aProblema(p.error));
        return p.data;
      }

      const r = await api.v1.clubs({ club }).announcements.post(
        {
          title: titulo.trim(),
          body: cuerpo.trim(),
          audiences: delContrato(audiencias),
          pinned: fijado,
          requires_ack: pideAcuse,
          publish: publicar,
        },
        // Clave nueva por pulsación: una guardada replicaría el aviso anterior y
        // diría que se mandó algo que no se mandó.
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: alTerminar,
    onError: (e) => setProblema(aProblema(e)),
  });

  const cambiar = (i: number, campo: keyof FilaDeAudiencia, valor: string) =>
    setAudiencias((f) =>
      f.map((fila, j) => (j === i ? { ...fila, [campo]: valor } : fila))
    );

  /** Si alguna fila pide elegir un grupo, un cinturón… */
  const pideA = (que: string) =>
    audiencias.some(
      (a) => AMBITOS.find((x) => x.valor === a.scope)?.pide === que
    );

  /*
   * Una audiencia está completa si no pide nada más, o si lo que pide está
   * elegido. Un `group` sin grupo mandaría el aviso a nadie —o peor, el servidor
   * lo rechazaría después de haberlo escrito entero—.
   */
  const completas = audiencias.every((a) => {
    const def = AMBITOS.find((x) => x.valor === a.scope);
    if (def?.pide === 'grupo') return a.group_id !== '';
    if (def?.pide === 'cinturon') return a.belt_rank_id !== '';
    // Sede y disciplina todavía no tienen selector: no se ofrecen en la lista.
    return def?.pide === 'nada';
  });

  const listo =
    titulo.trim().length > 0 &&
    cuerpo.trim().length > 0 &&
    (corrigiendo || (audiencias.length > 0 && completas));

  /*
   * A cuántas familias llega, mientras se eligen las audiencias. Solo con todas
   * completas: «un grupo» sin grupo elegido no es una audiencia, y el servidor la
   * rechazaría. Se guarda la cifra anterior mientras llega la nueva, para que el
   * botón no parpadee al cambiar un desplegable.
   */
  const paraElContrato = delContrato(audiencias);
  const alcance = useQuery({
    queryKey: ['alcance-de-aviso', club, paraElContrato],
    enabled: !corrigiendo && audiencias.length > 0 && completas,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .announcements.preview.post({ audiences: paraElContrato });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as { reach: number; students: number };
    },
  });

  /*
   * Las familias a las que llegaría: de la vista previa si es nuevo, del propio
   * aviso si es un borrador (el servidor calcula su `reach` al leerlo).
   */
  const familias = corrigiendo
    ? aviso.reach
    : completas
      ? alcance.data?.reach
      : undefined;
  // A nadie no se publica: «Guardar sin avisar» sigue disponible.
  const aNadie = familias === 0;

  return (
    <form className="formulario" onSubmit={(e) => e.preventDefault()}>
      <div className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCancelar}>
          ← Avisos
        </button>
      </div>

      <h1>{corrigiendo ? 'Corregir el aviso' : 'Escribir un aviso'}</h1>

      <div className="rejilla">
        <label>
          Título
          <input
            required
            maxLength={160}
            placeholder="El martes no hay clase"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
          />
        </label>
      </div>

      <label>
        El aviso
        <textarea
          required
          rows={6}
          maxLength={4000}
          placeholder="El polideportivo cierra por obras. Volvemos el jueves a la hora de siempre."
          value={cuerpo}
          onChange={(e) => setCuerpo(e.target.value)}
        />
      </label>

      {corrigiendo ? (
        <>
          {borrador ? (
            <div className="nota">
              <strong>A quién le llega ya no se cambia.</strong> Si va al sitio
              equivocado, retíralo y escribe otro.
            </div>
          ) : (
            <div className="nota">
              <strong>A quién llegó no se puede cambiar.</strong> Quien ya lo
              recibió no puede des-recibirlo, y quien no lo recibió no se
              enteraría. Si va al sitio equivocado, retíralo y publica otro.
            </div>
          )}
          <div className="aviso-audiencias">
            {aviso.audiences.map((a, i) => (
              <span key={i} className="pastilla pastilla-left">
                {etiquetaDeAudiencia(a)}
              </span>
            ))}
          </div>
        </>
      ) : (
        <>
          <h2 className="seccion">A quién le llega</h2>

          <p className="tenue">
            Se pueden poner varias y <strong>se suman</strong>: «los del martes»
            y «los verdes» llega a los dos grupos enteros, no a quien esté en
            los dos.
          </p>

          {/*
            `isLoading` y no `isPending`: la consulta de cinturones solo se lanza si
            el club tiene un sistema de grados en uso, y una consulta desactivada y
            sin datos está «pendiente» para siempre. Con `isPending`, un club sin
            sistema en uso veía este «Cargando» sin fin. 24-09-2026.
          */}
          {(grupos.isLoading || sistemas.isLoading || cinturones.isLoading) && (
            <Cargando filas={1} etiqueta="Cargando" />
          )}

          <ul className="audiencias-editor">
            {audiencias.map((a, i) => {
              const def = AMBITOS.find((x) => x.valor === a.scope);
              return (
                <li key={i}>
                  <select
                    value={a.scope}
                    onChange={(e) => cambiar(i, 'scope', e.target.value)}
                  >
                    {AMBITOS.filter(
                      // Sede y disciplina no tienen selector todavía: ofrecerlas
                      // sería ofrecer un camino que acaba en un aviso a nadie.
                      (o) => o.pide !== 'sede' && o.pide !== 'disciplina'
                    ).map((o) => (
                      <option key={o.valor} value={o.valor}>
                        {o.etiqueta}
                      </option>
                    ))}
                  </select>

                  {def?.pide === 'grupo' && (
                    <select
                      required
                      value={a.group_id}
                      onChange={(e) => cambiar(i, 'group_id', e.target.value)}
                    >
                      <option value="">Elige un grupo</option>
                      {(grupos.data ?? []).map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  )}

                  {def?.pide === 'cinturon' && (
                    <select
                      required
                      value={a.belt_rank_id}
                      onChange={(e) =>
                        cambiar(i, 'belt_rank_id', e.target.value)
                      }
                    >
                      <option value="">Elige un cinturón</option>
                      {(cinturones.data ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.short_name?.trim() || c.name}
                        </option>
                      ))}
                    </select>
                  )}

                  {audiencias.length > 1 && (
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() =>
                        setAudiencias((f) => f.filter((_, j) => j !== i))
                      }
                    >
                      Quitar
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          {pideA('grupo') && grupos.data?.length === 0 && (
            <FaltaAntes accion="Crear el primer grupo" destino="/grupos" nuevo>
              Tu club todavía no tiene grupos, así que no hay a cuál avisar:
              envíalo a «Todo el club». Si vas a crear el grupo, lo que lleves
              escrito aquí no se guarda.
            </FaltaAntes>
          )}

          {pideA('cinturon') && sistemas.data?.length === 0 && (
            <FaltaAntes
              accion="Elegir la disciplina"
              destino="/club/disciplinas"
            >
              Tu club no sigue todavía ninguna escalera de grados, así que no
              hay cinturones a los que avisar. La escalera llega con la
              disciplina.
            </FaltaAntes>
          )}

          <button
            type="button"
            className="btn-plano"
            onClick={() => setAudiencias((f) => [...f, nueva()])}
          >
            Añadir otro destinatario
          </button>

          {completas && alcance.data && (
            <p
              className={alcance.data.reach === 0 ? 'nota' : 'tenue'}
              role="status"
            >
              {alcance.data.reach > 0 ? (
                <>
                  Llega a{' '}
                  <strong>
                    {cuenta(alcance.data.reach, 'familia', 'familias')}
                  </strong>
                  {alcance.data.students !== alcance.data.reach &&
                    ` (${cuenta(alcance.data.students, 'alumno', 'alumnos')})`}
                  .
                </>
              ) : alcance.data.students > 0 ? (
                <>
                  <strong>No llega a ninguna familia.</strong>{' '}
                  {cuenta(alcance.data.students, 'alumno', 'alumnos')} en esa
                  audiencia, pero ninguno tiene todavía a su familia en Kiap: se
                  la invita desde la ficha de cada alumno, con «Añadir tutor»; a
                  un alumno adulto, como «El propio alumno».
                </>
              ) : (
                <strong>No hay nadie en esa audiencia.</strong>
              )}
            </p>
          )}

          {audiencias.some((a) => a.scope === 'debtors') && (
            <div className="nota">
              <strong>Este aviso va a quien debe dinero.</strong> Escríbelo
              pensando en que lo va a leer una familia que quizá no sabe que
              debe: es la conversación que hoy se tiene en la puerta y delante
              del niño.
            </div>
          )}
        </>
      )}

      <div className="rejilla">
        <label className="opcion">
          <input
            type="checkbox"
            checked={fijado}
            onChange={(e) => setFijado(e.target.checked)}
          />
          Fijarlo arriba del muro
        </label>

        <label className="opcion">
          <input
            type="checkbox"
            checked={pideAcuse}
            onChange={(e) => setPideAcuse(e.target.checked)}
          />
          Pedir que confirmen que lo han leído
        </label>
      </div>

      {problema && <PanelError problema={problema} />}

      {/*
        Dos botones y no una casilla «publicar»: con una casilla marcada por
        defecto, quien la pasa por alto interrumpe a 120 familias. Aquí hay que
        elegir, y lo que se pulsa por inercia no es lo que avisa a nadie.
      */}
      <div className="acciones">
        <button
          type="button"
          disabled={
            !listo ||
            guardar.isPending ||
            (aNadie && (!corrigiendo || borrador))
          }
          onClick={() => {
            setProblema(null);
            guardar.mutate(true);
          }}
        >
          {guardar.isPending
            ? 'Guardando…'
            : corrigiendo && !borrador
              ? 'Guardar los cambios'
              : familias === undefined
                ? 'Publicar y avisar'
                : `Publicar y avisar ${aFamilias(familias)}`}
        </button>

        {(!corrigiendo || borrador) && (
          <button
            type="button"
            className="btn-plano"
            disabled={!listo || guardar.isPending}
            onClick={() => {
              setProblema(null);
              guardar.mutate(false);
            }}
          >
            Guardar sin avisar
          </button>
        )}

        <button
          type="button"
          className="btn-plano"
          disabled={guardar.isPending}
          onClick={alCancelar}
        >
          Cancelar
        </button>
      </div>

      {(!corrigiendo || borrador) && (
        <p className="tenue">
          «Publicar y avisar» lo manda al móvil de esas familias ahora mismo.
          «Guardar sin avisar» lo deja como borrador, que no ve nadie, y lo
          publicas cuando quieras desde la lista de avisos.
        </p>
      )}
    </form>
  );
}
